import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiLink,
  MuiNotesToolTip,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextFieldWithSearchIcon,
  MuiTypography,
  OptionsDataType,
  RowProps,
} from "../../../../components";
import { MuiDateTimeField } from "../../../../components/dateTimeField/MuiDateTimeField";
import { MODULE_PERMISSIONS } from "../../../../helpers/constants";
import {
  buildActionMenuItems,
  checkFilterMatch,
  formatWithTimezone,
  getUniqueLabelOptions,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import { SearchUserDataResponse } from "../../../usersSearch/modelsAndSchemas/models";
import { SystemMessageSearchResultKey } from "../../modelsAndSchemas/models";
import { searchSystemMessageSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { SystemMessageResultWrapper } from "./SystemMessageSearchResults.style";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { usePermissions } from "../../../../hooks/usePermissions";

interface SystemMessageResultsProps {
  rows: SearchUserDataResponse[];
  isSearchClick: boolean;
  pageId: string;
  handleViewMessage: (id: string) => void;
  handleEditMessage: (id: string) => void;
  handleDeleteMessage: (id: string) => void;
  userCountryDateFormatWithTime: string;
}

export const SystemMessageResults: FC<SystemMessageResultsProps> = ({
  rows,
  isSearchClick,
  pageId,
  handleViewMessage,
  handleEditMessage,
  handleDeleteMessage,
  userCountryDateFormatWithTime,
}) => {
  const { commonLabels, fetchLabel, fetchComponentTitle } = useSiteCore();
  const { hasModulePermission } = usePermissions();

  const { commonARIALabel } = useNavigationSiteCore();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [uniqueCountries, setUniqueCountries] = useState<OptionsDataType[]>([]);
  const [uniqueLanguages, setUniqueLanguages] = useState<OptionsDataType[]>([]);
  const [uniquePersistence, setUniquePersistence] = useState<OptionsDataType[]>(
    [],
  );

  const [allUpdatedRows, setAllUpdatedRows] = useState<
    SearchUserDataResponse[]
  >([]);

  const { control, reset, getValues, setValue } = useForm({
    mode: "all",
    defaultValues: {
      [SystemMessageSearchResultKey.messageIdFormat]: "",
      [SystemMessageSearchResultKey.messageTitle]: "",
      [SystemMessageSearchResultKey.messageDescription]: "",
      [SystemMessageSearchResultKey.countryName]: "",
      [SystemMessageSearchResultKey.persistence]: "",
      [SystemMessageSearchResultKey.languageName]: "",
      [SystemMessageSearchResultKey.fromDate]: "",
      [SystemMessageSearchResultKey.toDate]: "",
    },
  });

  const getResultLabel = (key: string) =>
    fetchLabel(searchSystemMessageSiteCoreKeys.resultsKey, key);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  useEffect(() => {
    resetPagination();
    reset();
  }, [rows]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData = structuredClone(rows);

      localData?.forEach((item: SearchUserDataResponse) => {
        item.messageId = item.id;
        item.persistence = item.isPersistent
          ? commonLabels.yes
          : commonLabels.na;
      });

      const uniqueCountries = getUniqueLabelOptions(
        localData,
        SystemMessageSearchResultKey.countryName,
      );
      setUniqueCountries(uniqueCountries);
      const uniqueLanguages = getUniqueLabelOptions(
        localData,
        SystemMessageSearchResultKey.languageName,
      );
      setUniqueLanguages(uniqueLanguages);
      const uniquePersistence = getUniqueLabelOptions(
        localData,
        SystemMessageSearchResultKey.persistence,
      );
      setUniquePersistence(uniquePersistence);

      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);
    }
  }, [rows, siteCoreLoading]);

  const filters = useWatch({ control });

  const compareFromDateAndToDate = (
    item: SearchUserDataResponse,
    fromDate: string,
    toDate: string,
  ) => {
    // Compare timezone-converted dates directly as formatted strings
    let isFromDateMatch = true;
    if (fromDate) {
      const formattedItemFromDate =
        item?.fromDate && item?.timezone
          ? formatWithTimezone(
              item.fromDate,
              item.timezone,
              userCountryDateFormatWithTime,
            )
          : "";
      isFromDateMatch = formattedItemFromDate === fromDate;
    }

    let isToDateMatch = true;
    if (toDate) {
      const formattedItemToDate =
        item?.toDate && item?.timezone
          ? formatWithTimezone(
              item.toDate,
              item.timezone,
              userCountryDateFormatWithTime,
            )
          : "";
      isToDateMatch = formattedItemToDate === toDate;
    }

    return { isFromDateMatch, isToDateMatch };
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const {
        messageIdFormat,
        messageTitle,
        countryName,
        languageName,
        persistence,
        fromDate,
        toDate,
      } = getValues();
      const filteredData =
        allUpdatedRows?.filter((item) => {
          const messageIdMatch = checkFilterMatch(
            messageIdFormat,
            getValueOrBlank(item?.messageIdFormat),
          );
          const titleMatch = checkFilterMatch(
            messageTitle,
            getValueOrBlank(item?.messageTitle),
          );
          const countryMatch = checkFilterMatch(
            countryName,
            getValueOrBlank(item?.countryCodes),
          );
          const languageMatch = checkFilterMatch(
            languageName,
            getValueOrBlank(item?.languageName),
          );
          const persistenceMatch = checkFilterMatch(
            persistence,
            getValueOrBlank(item?.persistence),
          );
          const { isFromDateMatch, isToDateMatch } = compareFromDateAndToDate(
            item,
            fromDate,
            toDate,
          );

          return (
            messageIdMatch &&
            titleMatch &&
            countryMatch &&
            languageMatch &&
            persistenceMatch &&
            isFromDateMatch &&
            isToDateMatch
          );
        }) ?? [];
      setFilteredResults([...filteredData]);
      resetPagination();
    }
  }, [filters]);

  const columns = [
    {
      id: SystemMessageSearchResultKey.messageIdFormat,
      label: getResultLabel(searchSystemMessageSiteCoreKeys.messageId),
      className: "message-id-column",
      isEmptyValue: true,
      isSortVisible: true,
      render: (row: RowProps) =>
        !row.messageIdFormat ? null : (
          <MuiLink
            onClick={() => {
              handleViewMessage(row?.id);
            }}
            id={`${pageId}-view-message-${row.id}`}
            data-testid="view-message-link"
          >
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link"
                text={row.messageIdFormat}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
    },
    {
      id: SystemMessageSearchResultKey.messageTitle,
      label: getResultLabel(searchSystemMessageSiteCoreKeys.title),
      isSortVisible: true,
      isEmptyValue: true,
      className: "title-column",
    },
    {
      id: SystemMessageSearchResultKey.messageDescription,
      label: getResultLabel(searchSystemMessageSiteCoreKeys.message),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiNotesToolTip message={row?.messageDescription} />
      ),
    },
    {
      id: SystemMessageSearchResultKey.countryName,
      label: getResultLabel(searchSystemMessageSiteCoreKeys.resultsCountry),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SystemMessageSearchResultKey.languageName,
      label: getResultLabel(searchSystemMessageSiteCoreKeys.language),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SystemMessageSearchResultKey.persistence,
      label: getResultLabel(searchSystemMessageSiteCoreKeys.persistent),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SystemMessageSearchResultKey.fromDate,
      label: getResultLabel(searchSystemMessageSiteCoreKeys.fromDateTime),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => (
        <>
          {formatWithTimezone(
            row?.fromDate,
            row?.timezone,
            userCountryDateFormatWithTime,
          )}
        </>
      ),
    },
    {
      id: SystemMessageSearchResultKey.toDate,
      label: getResultLabel(searchSystemMessageSiteCoreKeys.toDateTime),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => (
        <>
          {formatWithTimezone(
            row?.toDate,
            row?.timezone,
            userCountryDateFormatWithTime,
          )}
        </>
      ),
    },
    {
      id: "action",
      label: getResultLabel(searchSystemMessageSiteCoreKeys.action),
      render: (row: RowProps) => {
        const menuItems = buildActionMenuItems(
          row,
          [
            {
              permissionKey: MODULE_PERMISSIONS.VIEW_SYSTEM_MESSAGES,
              create: (row) => ({
                onClick: () => handleViewMessage(row?.id),
                dataTestId: "view",
                label: commonLabels.view,
                ariaLabel: commonLabels.view,
              }),
            },
            {
              permissionKey: MODULE_PERMISSIONS.EDIT_SYSTEM_MESSAGES,
              create: (row) => ({
                onClick: () => handleEditMessage(row?.id),
                dataTestId: "edit",
                label: commonLabels.edit,
                ariaLabel: commonLabels.edit,
              }),
            },
            {
              permissionKey: MODULE_PERMISSIONS.DELETE_SYSTEM_MESSAGES,
              create: (row) => ({
                onClick: () => handleDeleteMessage(row?.id),
                dataTestId: "delete",
                label: commonLabels.delete,
                ariaLabel: commonLabels.delete,
              }),
            },
          ],
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  const tableHeaderForm = [
    <FormController
      control={control}
      name={SystemMessageSearchResultKey.messageIdFormat}
      key={SystemMessageSearchResultKey.messageIdFormat}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${SystemMessageSearchResultKey.messageIdFormat}`}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      name={SystemMessageSearchResultKey.messageTitle}
      key={SystemMessageSearchResultKey.messageTitle}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${SystemMessageSearchResultKey.messageTitle}`}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <></>,

    <FormController
      control={control}
      key={SystemMessageSearchResultKey.countryName}
      name={SystemMessageSearchResultKey.countryName}
    >
      <MuiSelect
        label=""
        options={uniqueCountries}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${SystemMessageSearchResultKey.countryName}`}
        requiredARIALabel={commonARIALabel.required}
        showSearch
        searchPlaceholder={commonLabels.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={SystemMessageSearchResultKey.languageName}
      name={SystemMessageSearchResultKey.languageName}
    >
      <MuiSelect
        label=""
        options={uniqueLanguages}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${SystemMessageSearchResultKey.languageName}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={SystemMessageSearchResultKey.persistence}
      name={SystemMessageSearchResultKey.persistence}
    >
      <MuiSelect
        label=""
        options={uniquePersistence}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${SystemMessageSearchResultKey.persistence}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,

    <FormController
      control={control}
      key={SystemMessageSearchResultKey.fromDate}
      name={SystemMessageSearchResultKey.fromDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateTimeField
          format={userCountryDateFormatWithTime}
          name={SystemMessageSearchResultKey.fromDate}
          clearable
          value={
            getValues(SystemMessageSearchResultKey.fromDate) !== ""
              ? dayjs(
                  getValues(SystemMessageSearchResultKey.fromDate),
                  userCountryDateFormatWithTime,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              SystemMessageSearchResultKey.fromDate,
              newValue?.format(userCountryDateFormatWithTime) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateTimeARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      key={SystemMessageSearchResultKey.toDate}
      name={SystemMessageSearchResultKey.toDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateTimeField
          format={userCountryDateFormatWithTime}
          name={SystemMessageSearchResultKey.toDate}
          clearable
          value={
            getValues(SystemMessageSearchResultKey.toDate) !== ""
              ? dayjs(
                  getValues(SystemMessageSearchResultKey.toDate),
                  userCountryDateFormatWithTime,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              SystemMessageSearchResultKey.toDate,
              newValue?.format(userCountryDateFormatWithTime) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateTimeARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <></>,
  ];

  return (
    <SystemMessageResultWrapper>
      <MuiBox className="result-header" data-testid="system-search-results">
        <MuiTypography variant="h2SemiBold" className="header-text">
          {isSearchClick
            ? fetchComponentTitle(searchSystemMessageSiteCoreKeys.resultsKey)
            : getResultLabel(searchSystemMessageSiteCoreKeys.allMessagesHeader)}
        </MuiTypography>
        {rows.length > 0 && (
          <MuiTypography variant="h3SemiBold" className="selected-count">
            {rows.length}
          </MuiTypography>
        )}
      </MuiBox>
      <MuiGrid container>
        <MuiGrid size={{ xs: 12 }}>
          <MuiTable
            ref={tableRef}
            pageId={pageId}
            tableHeaderForm={tableHeaderForm}
            columns={columns}
            tableResults={[...filteredResults]}
            noDataInApi={rows.length === 0}
            noResultMessage={commonLabels.noResult}
            priorityResults={[]}
            isLastColumnSticky
          />
        </MuiGrid>
      </MuiGrid>
    </SystemMessageResultWrapper>
  );
};
